import { useMemo, useRef, useState, useEffect } from "react";
import * as d3 from "d3";

const MARGIN = 50;
const BAR_PADDING = 0.2;
const TITLE_PADDING = 20;
type CircularBarplotProps = {
  data: { name: string; value: number }[];
  title: string
};

const abbreviateLabel = (label: string, maxLength = 15): string => {
  if (label.length <= maxLength) return label;
  let slice = label.slice(0,maxLength);
  slice = slice + '...' 
  return slice;
};

const CircularBarplot = ({ data, title }: CircularBarplotProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const resizeTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0].contentRect) {
        if (resizeTimeout.current) {
          clearTimeout(resizeTimeout.current);
        }
        resizeTimeout.current = setTimeout(() => {
          setDimensions({
            width: entries[0].contentRect.width,
            height: entries[0].contentRect.height
          });
        }, 10);
      }
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeout.current) {
        clearTimeout(resizeTimeout.current);
      }
    };
  }, []);

  const innerRadius = 90;
  const outerRadius = useMemo(
    () => Math.min(dimensions.width, dimensions.height) / 2 - MARGIN,
    [dimensions.width, dimensions.height]
  );

  const groups = useMemo(
    () => data.sort((a, b) => b.value - a.value).map(d => d.name),
    [data]
  );

  //Color de áreas
const colorScale = useMemo(() => {
   // Escala que mapea el nombre del grupo (dominio) a un color (rango)
   return d3.scaleOrdinal()
       .domain(groups) // El dominio es la lista de nombres de categorías
       .range(d3.schemeCategory10); // El rango son los 10 colores estándar de D3
}, [groups]);

  const xScale = useMemo(() => {
    return d3
      .scaleBand()
      .domain(groups)
      .range([0, 2 * Math.PI])
      .padding(BAR_PADDING);
  }, [groups]);

  const yScale = useMemo(() => {
    const max = d3.max(data, d => d.value) || 10;
    return d3
      .scaleRadial()
      .domain([0, max])
      .range([innerRadius, outerRadius]);
  }, [data, innerRadius, outerRadius]);

  const arcPathGenerator = useMemo(() => d3.arc<any, d3.DefaultArcObject>(), []);

  const allShapes = useMemo(
    () =>
      data.map((group, i) => {
        const path = arcPathGenerator({
          innerRadius: innerRadius,
          outerRadius: yScale(group.value),
          startAngle: xScale(group.name) as number,
          endAngle: xScale(group.name)! + xScale.bandwidth(),
        });

        const barAngle = xScale(group.name)! + xScale.bandwidth() / 2;
        const turnLabelUpsideDown = (barAngle + Math.PI) % (2 * Math.PI) < Math.PI;
        const labelRotation = (barAngle * 180) / Math.PI - 90;
        const labelXTranslation = yScale(group.value) + 10;
        const labelTransform = `rotate(${labelRotation}),translate(${labelXTranslation},0)`;

        return (
          <g key={i}>
            <path
              d={path as string}
              opacity={0.7}
              stroke={colorScale(group.name) as string}
              fill={colorScale(group.name) as string}
              fillOpacity={0.3}
              strokeWidth={1}
              rx={1}
            />
            <g transform={labelTransform}>
              <text
                textAnchor={turnLabelUpsideDown ? "end" : "start"}
                alignmentBaseline="middle"
                fontSize={9}
                transform={turnLabelUpsideDown ? "rotate(180)" : "rotate(0)"}
              >
                {abbreviateLabel(group.name,23)}
                <title>{group.name}</title>
              </text>
            </g>
          </g>
        );
      }),
    [arcPathGenerator, data, xScale, yScale, innerRadius]
  );

  return (
    <div ref={ref} style={{ width: "100%", height: "100%" }}>
      <svg width={dimensions.width} height={dimensions.height}>
      <text
          x={dimensions.width / 2}
          y={TITLE_PADDING}
          textAnchor="middle"
          alignmentBaseline="middle"
          fontSize={16}
          fontWeight="bold"
        >
          {title}
        </text>
        <g transform={`translate(${dimensions.width / 2}, ${(dimensions.height / 2) + TITLE_PADDING})`}>
          {allShapes}
        </g>
      </svg>
    </div>
  );
};

export default CircularBarplot;
